import { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";

import { Chip } from "ui/chip";

import { getDictionaryItemDescription } from "hooks/api/dictionary";

import { buildTouchpointPreviewHref, getTouchpointPaths } from "utilities/app-routes";
import { getDisplayTouchpointIcon, getDisplayTouchpointType, toSpacedTitleCase } from "utilities";
import { getTouchpointClassChipColor } from "utilities/touchpoint-class-chip";
import { formatDateTime } from "utilities/date";

import { NamedDictionaryItem } from "hooks/api/dictionary/dictionary.types";
import { TouchpointType, TouchpointTypeLabel } from "types/touchpoint";

import { PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";
import { TouchpointVersionHistoryDto } from "generated/schemas";

interface TableActionsProps {
  audienceDictionaryItems: NamedDictionaryItem[];
  hasTemplates: boolean;
}

const headerStyles = "text-gray-600 font-bold leading-5 pl-4";

export const createApprovedTableColumns = ({
  audienceDictionaryItems,
  hasTemplates,
}: TableActionsProps): ColumnDef<TouchpointVersionHistoryDto>[] => [
  {
    id: "touchpointType",
    header: "Type",
    accessorKey: "touchpointType",
    enableSorting: true,
    cell: ({ row }) => {
      const touchpointType = row.original.touchpointType as TouchpointType;
      const isTemplate = row.original.campaignName === PROGRAM_TEMPLATES_CAMPAIGN_NAME;
      const icon = getDisplayTouchpointIcon(touchpointType);
      const title =
        touchpointType === TouchpointType.OTHER
          ? TouchpointTypeLabel.OTHER
          : getDisplayTouchpointType(touchpointType);

      return (
        <div className="flex items-center gap-2 whitespace-nowrap">
          <div className="size-4 mt-0.5">{icon}</div>
          <span className="text-gray-800 font-medium">{title}</span>
          {isTemplate && <Chip variant="filled" size="small" label="Template" />}
        </div>
      );
    },
    size: hasTemplates ? 160 : 120,
    meta: {
      headerClassName: headerStyles,
    },
  },
  {
    id: "touchpointName",
    header: "Creative version",
    accessorKey: "touchpointName",
    enableSorting: true,
    cell: ({ row }) => {
      return (
        <div className="flex flex-col pr-3">
          <Link
            to={buildTouchpointPreviewHref(
              getTouchpointPaths({
                clientId: row.original.clientId ?? "",
                campaignId: row.original.campaignId ?? "",
                touchpointId: row.original.touchpointId ?? "",
              }).touchpointPreviewPath,
              row.original.touchpointVersionId,
            )}
            className="text-aqua-600 hover:text-aqua-700 font-semibold line-clamp-1"
            aria-disabled={false}
            tabIndex={undefined}>
            {row.original.touchpointName}
          </Link>
          <div>
            <span>v {row.original.touchpointVersionVersion}</span>
          </div>
        </div>
      );
    },
    size: 187,
    meta: {
      headerClassName: headerStyles,
    },
  },
  {
    id: "campaignName",
    header: "Campaign name",
    accessorKey: "campaignName",
    enableSorting: true,
    cell: ({ row }) => (
      <div>
        <span className="line-clamp-1">{row.original.campaignName}</span>
      </div>
    ),
    size: 177,
    meta: {
      headerClassName: headerStyles,
    },
  },
  {
    id: "campaignAudiences",
    header: "Audience",
    accessorKey: "campaignAudiences",
    enableSorting: false,
    cell: ({ row }) => {
      const audienceDescriptions = row.original.campaignAudiences?.map((audienceName) => ({
        name: audienceName,
        description: getDictionaryItemDescription(audienceDictionaryItems, audienceName),
      }));

      return (
        <div className="flex flex-wrap">
          {audienceDescriptions?.map(({ name, description }, index) => (
            <span key={name} className="whitespace-nowrap">
              {description}
              {index < audienceDescriptions.length - 1 && ",\u00A0"}
            </span>
          ))}
        </div>
      );
    },
    size: 187,
    meta: {
      headerClassName: headerStyles,
    },
  },
  {
    id: "populationType",
    header: "Population type",
    accessorKey: "populationType",
    enableSorting: true,
    cell: ({ row }) =>
      row.original.populationType ? (
        <Chip
          size="small"
          variant="outlined"
          label={toSpacedTitleCase(row.original.populationType)}
        />
      ) : null,
    size: 145,
    meta: {
      headerClassName: `${headerStyles} pl-4`,
    },
  },
  {
    id: "classes",
    header: "Class",
    accessorKey: "classes",
    enableSorting: false,
    cell: ({ row }) => {
      if (!row.original.classes?.length) {
        return null;
      }

      return (
        <div className="flex flex-wrap gap-1">
          {row.original.classes.map((classType) => (
            <Chip
              key={classType}
              size="small"
              variant="outlined"
              color={getTouchpointClassChipColor(classType)}
              label={toSpacedTitleCase(classType)}
            />
          ))}
        </div>
      );
    },
    size: 120,
    meta: {
      headerClassName: `${headerStyles} pl-4`,
    },
  },
  {
    id: "lastModifiedDate",
    header: "Last updated",
    accessorKey: "lastModifiedDate",
    enableSorting: true,
    cell: ({ row }) => (
      <div className="text-gray-600">{formatDateTime(row.original.lastModifiedDate)}</div>
    ),
    size: 125,
    meta: {
      headerClassName: headerStyles,
    },
  },
];
